import session from './session'
import { getEnvironment, getPartnerKey } from 'shared/javascripts/util/environment'
import { isNativeApp, isIOS } from 'shared/javascripts/util/detection'
import { amplitudePush } from './amplitude'
import { gtagPush } from './google_tag_manager'
import { metaPixelPush } from './meta_pixel'
import { tikTokPixelPush } from './tik_tok_pixel'
import { trackStats } from './stats_endpoint'
import { inOverlay } from 'shared/javascripts/util/unpoly_compat'
import { domTrackingContext } from '../tracking/dom'

type EventData = Record<string, any>

export interface TrackEventOverrides {
  nativeApp?: boolean
}

export interface TrackEventOptions {
  /** The element whose `data-tracking-context` ancestors supply context for the push. */
  from?: Element
  overrides?: TrackEventOverrides
}

// The sink: every event ends up here. Which events exist is defined in doc/tracking/.
export function trackEvent(
  eventName: string,
  customEventData: EventData = {},
  { from, overrides }: TrackEventOptions = {},
) {
  // Context from the element's ancestors, beneath whatever the caller passes explicitly.
  const eventData: EventData = {
    ...(from ? domTrackingContext(from) : {}),
    ...customEventData,
  }

  const partnerKey = getPartnerKey()
  if (partnerKey) {
    eventData.partnerKey = partnerKey
  }

  const userData: Record<string, any> = {
    subjectArea: session.subjectAreaKey,
    seenVideosCount: session.seenVideoIds.length,
    role: session.role,
    confirmed: session.emailConfirmed,
    nativeApp: isNativeApp(),
    nativeAppPlatform: isIOS() ? 'iOS' : 'android',
    educationLevel: session.educationLevel,
    academyUser: session.academyPartnerName,
    academy_function: session.academyFunction,
    academy_partner_id: session.academyPartnerId,
    sessionIdentifier: session.sessionIdentifier,
  }

  trackStats(eventName, eventData)

  gtagPush({
    event: eventName,
    eventData,
    currentUser: {
      id: session.userId,
      ...userData,
      ...overrides,
    },
    path: location.pathname,
    env: getEnvironment(),
  })

  amplitudePush(
    eventName,
    Object.assign({}, eventData, {
      path: location.pathname,
    }),
    userData,
  )

  metaPixelPush(eventName, eventData)
  tikTokPixelPush(eventName)
}

export function trackElementInteraction(
  element: HTMLElement,
  eventName: string,
  customEventData: EventData = {},
) {
  const upIdElement = element.closest('[up-id]')
  const { text, ...restEventData } = customEventData

  trackEvent(
    eventName,
    {
      element: {
        id: upIdElement
          ? upIdElement.getAttribute('up-id')
          : Array.from(element.classList)
              .filter((klass) => !klass.startsWith('up-'))
              .join(' '),
        // `??`, not `||`: a caller passing '' means "this element's text must not be sent", which is how
        // an element whose text is a name, an e-mail address or something the user typed opts out.
        text: text ?? element.textContent.trim().replace(/\s+/g, ' '),
        modal: inOverlay(),
      },
      ...restEventData,
    },
    { from: element },
  )
}
